import React, { useEffect, useState } from "react";
import { Box, Typography, Chip, Alert, CircularProgress, IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useImpersonation } from "../../contexts/ImpersonationContext";

export default function ImpersonationAuditDetail() {
  const { sessionId } = useParams();
  const { listAudit } = useImpersonation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventos, setEventos] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listAudit(Number(sessionId));
        if (!cancelled) setEventos(data?.eventos || []);
      } catch (e) {
        if (!cancelled) setError(`Error: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, listAudit]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Tooltip title="Volver">
          <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
          Sesión de impersonation #{sessionId}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : eventos.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#64748b" }}>
          Sin eventos registrados.
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1 }}>
          {eventos.map((e) => <EventCard key={e.id} e={e} />)}
        </Box>
      )}
    </Box>
  );
}

function EventCard({ e }) {
  return (
    <Box sx={{
      p: 1.4, borderRadius: 1.5, bgcolor: "#fff",
      border: e.es_critica ? "1px solid #fecaca" : "1px solid #e2e8f0",
      display: "grid", gridTemplateColumns: { xs: "1fr", sm: "180px 1fr auto" },
      gap: 1.2, alignItems: "center",
    }}>
      <Box>
        <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
          {new Date(e.ts).toLocaleString()}
        </Typography>
        {e.ip && (
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
            IP {e.ip}
          </Typography>
        )}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
          {e.accion}
          {e.entidad ? <span style={{ color: "#64748b", fontWeight: 500 }}> · {e.entidad}{e.entidad_id ? ` #${e.entidad_id}` : ""}</span> : null}
        </Typography>
        {e.detalle && Object.keys(e.detalle).length > 0 && (
          <Typography sx={{ fontSize: 11.5, color: "#64748b", fontFamily: "monospace", mt: 0.4 }}>
            {summarize(e.detalle)}
          </Typography>
        )}
      </Box>
      <Box>
        {e.es_critica && <Chip size="small" color="error" label="CRÍTICA" />}
      </Box>
    </Box>
  );
}

function summarize(d) {
  try {
    const parts = [];
    for (const [k, v] of Object.entries(d || {})) {
      let vs = typeof v === "string" ? v : JSON.stringify(v);
      if (vs.length > 80) vs = vs.slice(0, 77) + "…";
      parts.push(`${k}=${vs}`);
    }
    return parts.join(" · ");
  } catch (_) {
    return "";
  }
}
